import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Avatar from '@mui/material/Avatar';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { keyframes } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  getForestTeacherOverview,
  getTeacherAppreciationCandidates,
  sendForestMotivationMessage,
} from '../api/vks';
import { fetchClasses } from '../stargarden/api/starGardenApi';

const todayIso = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const currentMonthIso = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
};

const phaseEmoji = {
  uyanis: '🌱',
  yeserme: '🌿',
  buyume: '🌳',
  ciceklenme: '🌸',
};

const phaseLabel = (phase) => {
  const key = String(phase || '').toLowerCase();
  if (key === 'uyanis') return 'Uyanış';
  if (key === 'yeserme') return 'Yeşerme';
  if (key === 'buyume') return 'Büyüme';
  if (key === 'ciceklenme') return 'Çiçeklenme';
  return '-';
};

const pulseColorByLevel = {
  low: 'warning',
  medium: 'info',
  high: 'success',
};

const riskMeta = {
  aktif: { label: 'Aktif', color: 'success' },
  dusus: { label: 'Düşüş', color: 'info' },
  takip_yok: { label: 'Henüz Düzenli Değil', color: 'warning' },
};

const forestSway = keyframes`
  0% { transform: rotate(0deg) translateY(0px); }
  50% { transform: rotate(1.2deg) translateY(-2px); }
  100% { transform: rotate(0deg) translateY(0px); }
`;

const leafDrift = keyframes`
  0% { transform: translateY(0px) translateX(0px) scale(1); opacity: 0.12; }
  50% { transform: translateY(-10px) translateX(6px) scale(1.05); opacity: 0.2; }
  100% { transform: translateY(0px) translateX(0px) scale(1); opacity: 0.12; }
`;

const dropPop = keyframes`
  0% { opacity: 0; transform: translateY(10px) scale(0.85); }
  20% { opacity: 1; transform: translateY(0px) scale(1); }
  100% { opacity: 0; transform: translateY(-14px) scale(1.03); }
`;

const phaseBloom = keyframes`
  0% { opacity: 0.88; transform: scale(0.96); filter: saturate(0.9); }
  100% { opacity: 1; transform: scale(1); filter: saturate(1); }
`;

const sparkleDrift = keyframes`
  0% { opacity: 0; transform: translateY(6px) scale(0.75); }
  35% { opacity: 0.95; transform: translateY(-2px) scale(1); }
  100% { opacity: 0; transform: translateY(-16px) scale(1.08); }
`;

const sproutSway = keyframes`
  0% { transform: rotate(0deg) translateY(0px); }
  50% { transform: rotate(-4deg) translateY(-1px); }
  100% { transform: rotate(0deg) translateY(0px); }
`;

const dewTwinkle = keyframes`
  0% { opacity: 0.2; transform: scale(0.85); }
  50% { opacity: 0.92; transform: scale(1); }
  100% { opacity: 0.2; transform: scale(0.85); }
`;

const leafConfig = [
  { left: '6%', top: '22%', delay: '0s', duration: '7.2s' },
  { left: '19%', top: '56%', delay: '0.8s', duration: '8.1s' },
  { left: '34%', top: '18%', delay: '1.2s', duration: '7.4s' },
  { left: '52%', top: '64%', delay: '0.5s', duration: '8.8s' },
  { left: '74%', top: '20%', delay: '1.6s', duration: '8.2s' },
  { left: '88%', top: '58%', delay: '0.3s', duration: '7.9s' },
];

const phaseBannerMeta = {
  uyanis: {
    title: 'Uyanış Ormanı',
    subtitle: 'İlk damlalarla birlikte orman canlanmaya başlar.',
    bannerBg: 'rgba(243, 251, 238, 0.92)',
    bannerBorder: 'rgba(137, 180, 116, 0.28)',
    landscapeBg:
      'radial-gradient(46% 62% at 18% 5%, rgba(255,243,201,0.6) 0%, rgba(255,243,201,0) 100%), linear-gradient(180deg, rgba(248,252,239,0.9) 0%, rgba(236,247,224,0.96) 55%, rgba(191,220,160,0.72) 100%)',
    leafCount: 0,
    swayDuration: '7s',
    icons: [],
  },
  yeserme: {
    title: 'Yeşeren Orman',
    subtitle: 'Sınıf ritmi güçlendikçe orman canlanır.',
    bannerBg: 'rgba(228, 247, 233, 0.9)',
    bannerBorder: 'rgba(76, 175, 80, 0.24)',
    landscapeBg:
      'linear-gradient(180deg, rgba(241,251,244,0.66) 0%, rgba(223,245,229,0.84) 50%, rgba(145,204,134,0.5) 50%, rgba(112,184,103,0.62) 100%)',
    leafCount: 4,
    swayDuration: '5.8s',
    icons: ['🌱', '🌿', '🌳', '🌳'],
  },
  buyume: {
    title: 'Büyüyen Orman',
    subtitle: 'Düzenli katkıyla orman daha güçlü bir gölge verir.',
    bannerBg: 'rgba(221, 245, 229, 0.92)',
    bannerBorder: 'rgba(56, 142, 60, 0.28)',
    landscapeBg:
      'linear-gradient(180deg, rgba(232,248,236,0.62) 0%, rgba(213,240,221,0.84) 47%, rgba(120,188,110,0.58) 47%, rgba(73,153,68,0.66) 100%)',
    leafCount: 5,
    swayDuration: '5.2s',
    icons: ['🌿', '🌳', '🌳', '🌳', '🌳'],
  },
  ciceklenme: {
    title: 'Çiçeklenen Orman',
    subtitle: 'Katılım zirveye yaklaştıkça orman çiçeklenir.',
    bannerBg: 'rgba(241, 249, 232, 0.94)',
    bannerBorder: 'rgba(67, 160, 71, 0.32)',
    landscapeBg:
      'linear-gradient(180deg, rgba(247,252,238,0.62) 0%, rgba(232,247,225,0.86) 48%, rgba(133,197,121,0.56) 48%, rgba(85,166,79,0.66) 100%)',
    leafCount: 6,
    swayDuration: '4.8s',
    icons: ['🌿', '🌳', '🌳', '🌸', '🌳', '🌸'],
  },
};

const forestGlyphByPhase = {
  uyanis: ['🌱', '🌱', '🌿', '🌱'],
  yeserme: ['🌿', '🌱', '🌿', '🌱'],
  buyume: ['🌳', '🌿', '🌳', '🌱'],
  ciceklenme: ['🌸', '🌳', '🌸', '🌿'],
};

const uyanisSprouts = [
  { left: 14, delay: '0s', scale: 0.88, bottom: 19 },
  { left: 33, delay: '0.5s', scale: 0.98, bottom: 20 },
  { left: 51, delay: '0.9s', scale: 1.04, bottom: 21 },
  { left: 69, delay: '0.6s', scale: 0.94, bottom: 20 },
  { left: 87, delay: '1.2s', scale: 0.86, bottom: 19 },
];

const audienceMeta = {
  all_parents: 'Tüm veliler',
  low_segment: 'Düşük segment veliler',
  selected_parent: 'Seçili veli',
};

function SectionCard({ title, subtitle, action, children }) {
  return (
    <Paper
      elevation={2}
      sx={{
        borderRadius: 3,
        p: { xs: 1.5, sm: 2 },
        bgcolor: 'rgba(255,255,255,0.88)',
        backdropFilter: 'blur(8px)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1,
          mb: 1.5,
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {title}
          </Typography>
          {!!subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        {action}
      </Box>
      {children}
    </Paper>
  );
}

function TrendBars({ items }) {
  const maxValue = Math.max(1, ...items.map((it) => Number(it?.active_parents_count || 0)));
  const weekdayLabel = (isoDate) => {
    const dt = new Date(String(isoDate || ''));
    if (Number.isNaN(dt.getTime())) return '-';
    return dt.toLocaleDateString('tr-TR', { weekday: 'short' });
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.1, minHeight: 170 }}>
      {items.map((item) => {
        const activeCount = Number(item?.active_parents_count || 0);
        const dropCount = Number(item?.drops_added || 0);
        const pct = Math.max(0, Math.min(100, (activeCount / maxValue) * 100));
        return (
          <Box
            key={String(item?.date || '')}
            sx={{
              width: '100%',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.7,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {activeCount}
            </Typography>
            <Box
              sx={{
                width: '100%',
                maxWidth: 42,
                height: 110,
                borderRadius: 999,
                bgcolor: 'rgba(67,160,71,0.15)',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'flex-end',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: `${pct}%`,
                  borderRadius: 999,
                  background: 'linear-gradient(180deg, #81c784 0%, #43a047 100%)',
                }}
                title={`${item?.date || ''} - Aktif: ${activeCount}, Damla: ${dropCount}`}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              {weekdayLabel(item?.date)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              +{dropCount}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function getWeeklyPulseNarrative(activeParents, drops) {
  const active = Math.max(0, Number(activeParents || 0));
  const totalDrops = Math.max(0, Number(drops || 0));
  if (active <= 0 && totalDrops <= 0) {
    return '🌿 Son 7 günde ormanımıza yeni bir hareket eklenmedi.';
  }
  if (active <= 0) {
    return `🌿 Son 7 günde ${totalDrops} damla oluştu.`;
  }
  return `🌿 Son 7 günde ${active} aktif veli katkısı ve ${totalDrops} damla oluştu.`;
}

function getWeeklyPulseStatusMeta(activeParents, previousActiveParents) {
  const current = Math.max(0, Number(activeParents || 0));
  const previous = Math.max(0, Number(previousActiveParents || 0));
  if (current <= 0) {
    return { level: 'low', message: 'Bu hafta yeni katkı bekleniyor.' };
  }
  if (previous > 0 && current < previous * 0.85) {
    return { level: 'low', message: 'Bu hafta ritim geçen haftanın altında.' };
  }
  if (previous > 0 && current > previous * 1.1) {
    return { level: 'high', message: 'Bu hafta ritim geçen haftadan daha güçlü.' };
  }
  return { level: 'medium', message: 'Bu hafta sınıf ritmi dengeli ilerliyor.' };
}

function getHeroComment(progressPct) {
  const pct = Number(progressPct || 0);
  if (pct <= 10) {
    return 'Orman yeni yeni canlanıyor. Küçük bir hatırlatma faydalı olabilir.';
  }
  if (pct <= 45) {
    return 'Ritim oluşuyor. Düzenli küçük adımlar ormanı hızla büyütür.';
  }
  if (pct <= 85) {
    return 'Sınıf ritmi iyi gidiyor. Düzenli katkı sürüyor.';
  }
  if (pct < 100) {
    return 'Hedefe çok yaklaşıldı. Küçük bir teşvik mesajı sınıfı hedefe taşıyabilir.';
  }
  return 'Sınıf hedefi aştı. Emek veren velilere teşekkür zamanı.';
}

function getTrendComment(last7ActiveTotal, prev7ActiveTotal) {
  const current = Number(last7ActiveTotal || 0);
  const previous = Number(prev7ActiveTotal || 0);
  if (current <= 0) {
    return 'Henüz trend gösterecek kadar veri oluşmadı.';
  }
  if (previous > 0 && current < previous * 0.85) {
    return 'Son 7 gün ritmi önceki haftaya göre daha düşük.';
  }
  if (previous > 0 && current > previous * 1.1) {
    return 'Son 7 gün ritmi önceki haftaya göre daha güçlü.';
  }
  return 'Sınıf ritmi dengeli şekilde devam ediyor.';
}

const suggestionMetaByType = {
  daily_low_rhythm: {
    title: 'Bugün katılım düşük',
    body: 'Bugünkü ritim sınıf ortalamasının altında. Kısa bir motivasyon mesajı faydalı olabilir.',
    suggestedMessage: 'Merhaba veliler, bugün ormanımız biraz sessiz kaldı 🌿 Küçük bir çalışma bile büyük fark oluşturur.',
  },
  weekly_drop: {
    title: 'Haftalık ritimde düşüş var',
    body: 'Son 7 gün katılımı önceki haftaya göre daha düşük görünüyor.',
    suggestedMessage: 'Son günlerde ritmimiz biraz yavaşladı. Bu hafta küçük ama düzenli katkılarla ormanımızı birlikte güçlendirelim.',
  },
  near_target: {
    title: 'Sınıf hedefe çok yakın',
    body: 'Bu ay hedefe çok az kaldı. Kısa bir teşvik sınıfı hedefe taşıyabilir.',
    suggestedMessage: 'Harika bir ritim yakaladık 🌱 Bu ayki hedefe çok az kaldı, son adımı birlikte atalım.',
  },
  strong_rhythm: {
    title: 'Sınıf ritmi güçlü',
    body: 'Sınıf düzenli katılımla güçlü bir ivme yakaladı.',
    suggestedMessage: 'Bu ay gösterdiğiniz düzenli destek için teşekkür ederiz 🌿 Birlikte çok güzel bir ritim oluşturduk.',
  },
};

function getSuggestionMeta(type) {
  const key = String(type || '').trim();
  return suggestionMetaByType[key] || {
    title: 'Öneri',
    body: 'Sınıf ritmine göre kısa bir teşvik mesajı paylaşabilirsiniz.',
    suggestedMessage: 'Merhaba veliler, bu hafta küçük ama düzenli adımlarla ormanımıza birlikte katkı sağlayalım. 🌿',
  };
}

export default function ForestTeacherView({
  schoolId,
  onError,
  onBack,
}) {
  const [loading, setLoading] = useState(true);
  const [classLoading, setClassLoading] = useState(true);
  const [error, setError] = useState('');
  const [monthKey, setMonthKey] = useState(currentMonthIso());
  const [localDate, setLocalDate] = useState(todayIso());
  const [className, setClassName] = useState('');
  const [classes, setClasses] = useState([]);

  const [overview, setOverview] = useState(null);
  const [parentFilter, setParentFilter] = useState('all');
  const [sendLoading, setSendLoading] = useState(false);
  const [sendInfo, setSendInfo] = useState('');
  const [dropFx, setDropFx] = useState({ show: false, amount: 0 });
  const [phaseFxOn, setPhaseFxOn] = useState(false);
  const [appreciationLoading, setAppreciationLoading] = useState(false);
  const [appreciationError, setAppreciationError] = useState('');
  const [appreciationCandidate, setAppreciationCandidate] = useState(null);

  const [messageDialog, setMessageDialog] = useState({
    open: false,
    title: '',
    body: '',
    audience: 'all_parents',
    studentId: null,
    parentId: null,
    lockedAudience: false,
  });
  const dropFxTimerRef = useRef(null);
  const phaseFxTimerRef = useRef(null);
  const previousPhaseRef = useRef('');
  const dropFxContextRef = useRef('');
  const dropFxShownKeyRef = useRef('');

  const handleBack = useCallback(() => {
    if (typeof onBack === 'function') {
      onBack();
    }
  }, [onBack]);

  useEffect(() => {
    if (!schoolId) return;
    let cancelled = false;

    (async () => {
      setClassLoading(true);
      setError('');
      try {
        const list = await fetchClasses(schoolId);
        if (cancelled) return;

        const sorted = (list || [])
          .map((item) => ({
            id: item?.id,
            name: String(item?.name || '').trim(),
          }))
          .filter((item) => item.name)
          .sort((a, b) => a.name.localeCompare(b.name, 'tr-TR'));

        setClasses(sorted);

        if (!sorted.length) {
          setClassName('');
          setOverview(null);
          setError('Bu okula ait sınıf bulunamadı.');
          return;
        }

        if (sorted.length === 1) {
          setClassName(sorted[0].name);
          return;
        }

        setClassName((prev) => (sorted.some((cls) => cls.name === prev) ? prev : ''));
      } catch (e) {
        if (cancelled) return;
        const message = 'Sınıflar yüklenemedi.';
        setError(message);
        onError?.(message);
      } finally {
        if (!cancelled) {
          setClassLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [schoolId, onError]);

  const load = useCallback(async () => {
    if (!schoolId) return;
    if (!classes.length) {
      setLoading(false);
      return;
    }
    if (classes.length > 1 && !className) {
      setOverview(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const payload = await getForestTeacherOverview(schoolId, {
        className,
        monthKey,
        localDate,
        parentLimit: 200,
      });
      setOverview(payload || null);
    } catch (e) {
      const message = 'Orman verisi şu an yüklenemedi. Lütfen tekrar deneyin.';
      setError(message);
      onError?.(message);
    } finally {
      setLoading(false);
    }
  }, [schoolId, classes, className, monthKey, localDate, onError]);

  useEffect(() => {
    load();
  }, [load]);

  const hero = overview?.hero || {};
  const pulse = overview?.daily_pulse || {};
  const weeklyPulse = overview?.weekly_pulse || {};
  const trend = overview?.trend_7d || {};
  const distribution = overview?.distribution || {};
  const suggestions = Array.isArray(overview?.suggestions) ? overview.suggestions : [];
  const allParents = useMemo(
    () => (Array.isArray(overview?.parents?.items) ? overview.parents.items : []),
    [overview]
  );
  const archive = Array.isArray(overview?.archive) ? overview.archive : [];
  const capabilities = overview?.capabilities || {};

  const filteredParents = useMemo(() => {
    if (parentFilter === 'all') return allParents;
    if (parentFilter === 'active') {
      return allParents.filter((row) => String(row?.risk_bucket || '') === 'aktif');
    }
    if (parentFilter === 'takip_yok') {
      return allParents.filter((row) => String(row?.risk_bucket || '') === 'takip_yok');
    }
    if (parentFilter === 'dusus') {
      return allParents.filter((row) => String(row?.risk_bucket || '') === 'dusus');
    }
    if (parentFilter === 'awarded') {
      return allParents.filter((row) => !!row?.teacher_appreciation_awarded);
    }
    return allParents;
  }, [allParents, parentFilter]);

  const progressPct = Number(hero?.progress_pct || 0);
  const weeklyActiveParents = Number(weeklyPulse?.active_parents || 0);
  const weeklyDrops = Number(weeklyPulse?.drops || 0);
  const previousWeeklyActiveParents = Number(weeklyPulse?.previous_active_parents || 0);
  const previousWeeklyDrops = Number(weeklyPulse?.previous_drops || 0);
  const weeklyPulseStatus = getWeeklyPulseStatusMeta(weeklyActiveParents, previousWeeklyActiveParents);
  const phase = String(hero?.phase || '').toLowerCase();
  const phaseTitle = phaseLabel(phase);
  const isUyanis = phase === 'uyanis';
  const phaseIcon = phaseEmoji[phase] || '🌱';
  const bannerMeta = phaseBannerMeta[phase] || phaseBannerMeta.yeserme;
  const forestGlyphs = forestGlyphByPhase[phase] || forestGlyphByPhase.uyanis;
  const heroDropsTotal = Number(hero?.drops_total || 0);
  const heroTargetDrops = Number(hero?.target_drops || 0);
  const activeLeaves = useMemo(
    () => leafConfig.slice(0, Math.max(0, Number(bannerMeta?.leafCount || 4))),
    [bannerMeta]
  );

  const handleOpenSuggestion = useCallback((item) => {
    const meta = getSuggestionMeta(item?.type);
    setMessageDialog({
      open: true,
      title: String(meta.title || 'Motivasyon Mesajı'),
      body: String(meta.suggestedMessage || ''),
      audience: String(item?.audience_type || 'all_parents'),
      studentId: null,
      parentId: null,
      lockedAudience: false,
    });
  }, []);

  const handleOpenRowMessage = useCallback((row) => {
    const studentName = String(row?.student_name || 'Öğrenci');
    setMessageDialog({
      open: true,
      title: `${studentName} velisine mesaj`,
      body: `Merhaba, ${studentName} için bu ay sınıf ritmini birlikte güçlendirdiğiniz için teşekkür ederiz 🌿`,
      audience: 'selected_parent',
      studentId: Number(row?.student_id || 0) || null,
      parentId: Number(row?.parent_id || 0) || null,
      lockedAudience: true,
    });
  }, []);

  const handleOpenAppreciationMessage = useCallback(() => {
    if (!appreciationCandidate) return;
    const studentName = String(appreciationCandidate?.student_first_name || 'Öğrenci');
    const parentName = String(appreciationCandidate?.parent_first_name || 'Velimiz');
    const activeDays = Number(appreciationCandidate?.monthly_active_days || 0);

    setMessageDialog({
      open: true,
      title: `Ayın velisi adayı: ${parentName}`,
      body: `Merhaba ${parentName}, ${studentName} için bu ay gösterdiğiniz düzenli katkı (${activeDays} aktif gün) için teşekkür ederiz 🌿`,
      audience: 'selected_parent',
      studentId: Number(appreciationCandidate?.student_id || 0) || null,
      parentId: Number(appreciationCandidate?.parent_id || 0) || null,
      lockedAudience: true,
    });
  }, [appreciationCandidate]);

  const handleCopyMessage = useCallback(async () => {
    const text = String(messageDialog?.body || '').trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setMessageDialog((prev) => ({ ...prev, open: false }));
    } catch (e) {
      setError('Mesaj kopyalanamadı. Tarayıcı izinlerini kontrol edin.');
    }
  }, [messageDialog]);

  const handleSendMessage = useCallback(async () => {
    const text = String(messageDialog?.body || '').trim();
    if (!text) {
      setError('Mesaj metni boş olamaz.');
      return;
    }

    try {
      setSendLoading(true);
      setError('');
      const result = await sendForestMotivationMessage(schoolId, {
        className,
        monthKey,
        audienceType: String(messageDialog?.audience || 'all_parents'),
        messageText: text,
        studentId: messageDialog?.studentId,
        parentId: messageDialog?.parentId,
      });

      const queued = Number(result?.queued_count || 0);
      const attempted = Number(result?.attempted_count || 0);
      const skipped = Number(result?.skipped_count || 0);
      setSendInfo(`Kuyruğa eklendi: ${queued}/${attempted} mesaj, ${skipped} atlandı.`);
      setMessageDialog((prev) => ({ ...prev, open: false }));
    } catch (e) {
      setError('Mesaj gönderilemedi. Lütfen tekrar deneyin.');
    } finally {
      setSendLoading(false);
    }
  }, [schoolId, className, monthKey, messageDialog]);

  const monthLabel = useMemo(() => {
    const text = String(monthKey || '').trim();
    if (!text) return '-';
    try {
      const [y, m] = text.split('-');
      const dt = new Date(Number(y), Number(m) - 1, 1);
      return dt.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
    } catch (_) {
      return text;
    }
  }, [monthKey]);

  const triggerDropFx = useCallback((amount) => {
    const safeAmount = Math.max(1, Number(amount || 0));
    setDropFx({ show: true, amount: safeAmount });

    if (dropFxTimerRef.current) {
      clearTimeout(dropFxTimerRef.current);
    }
    dropFxTimerRef.current = setTimeout(() => {
      setDropFx({ show: false, amount: 0 });
    }, 1900);
  }, []);

  useEffect(() => {
    const current = phase;
    if (!current) return;

    const previous = previousPhaseRef.current;
    if (previous && previous !== current) {
      setPhaseFxOn(true);
      if (phaseFxTimerRef.current) {
        clearTimeout(phaseFxTimerRef.current);
      }
      phaseFxTimerRef.current = setTimeout(() => {
        setPhaseFxOn(false);
      }, 1200);
    }
    previousPhaseRef.current = current;
  }, [phase]);

  useEffect(() => {
    if (!schoolId || !className) return;
    const currentDrops = Number(hero?.drops_total || 0);
    if (!Number.isFinite(currentDrops) || currentDrops < 0) return;
    const todayDrops = Number(pulse?.drops_added_today || 0);

    const contextKey = `${schoolId}|${className}|${monthKey}`;
    const storageKey = `vks_forest_last_seen_drops_${contextKey}`;
    const isFreshContext = dropFxContextRef.current !== contextKey;
    if (isFreshContext) {
      dropFxContextRef.current = contextKey;
    }
    const shownKey = `${contextKey}|${String(localDate || '')}`;

    let previousDrops = null;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw !== null) {
        const parsed = Number(raw);
        if (Number.isFinite(parsed)) previousDrops = parsed;
      }
    } catch (_) {
      previousDrops = null;
    }

    // 1) Show when total drops increased since the last seen value.
    const delta = previousDrops !== null ? currentDrops - previousDrops : 0;
    if (delta > 0) {
      triggerDropFx(delta);
      dropFxShownKeyRef.current = shownKey;
    }

    // 2) Fallback: on first screen/context open, if there are drops today, show mini sparkle once.
    if (delta <= 0 && isFreshContext && todayDrops > 0 && dropFxShownKeyRef.current !== shownKey) {
      triggerDropFx(todayDrops);
      dropFxShownKeyRef.current = shownKey;
    }

    try {
      window.localStorage.setItem(storageKey, String(currentDrops));
    } catch (_) {
      // Ignore storage write failures; UI fallback still works.
    }
  }, [schoolId, className, monthKey, localDate, hero?.drops_total, pulse?.drops_added_today, triggerDropFx]);

  useEffect(() => {
    if (!schoolId || !className) {
      setAppreciationCandidate(null);
      setAppreciationError('');
      setAppreciationLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setAppreciationLoading(true);
        setAppreciationError('');
        const res = await getTeacherAppreciationCandidates(schoolId, {
          className,
          monthKey,
          limit: 1,
        });
        if (cancelled) return;
        const first = Array.isArray(res?.items) && res.items.length ? res.items[0] : null;
        setAppreciationCandidate(first);
      } catch (e) {
        if (cancelled) return;
        setAppreciationCandidate(null);
        setAppreciationError('Ayın velisi önerisi şu an alınamıyor.');
      } finally {
        if (!cancelled) setAppreciationLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [schoolId, className, monthKey]);

  useEffect(() => {
    return () => {
      if (dropFxTimerRef.current) {
        clearTimeout(dropFxTimerRef.current);
      }
      if (phaseFxTimerRef.current) {
        clearTimeout(phaseFxTimerRef.current);
      }
    };
  }, []);

  if (classLoading || loading) {
    return (
      <Box
        sx={{
          minHeight: '100dvh',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 1.2,
          background: 'linear-gradient(180deg, #e7f7ff 0%, #d9f2e5 55%, #fef6da 100%)',
        }}
      >
        <CircularProgress />
        <Typography sx={{ color: 'text.secondary', fontWeight: 700 }}>
          Gelecek Ormanı yükleniyor...
        </Typography>
      </Box>
    );
  }

  if (!loading && classes.length > 1 && !className) {
    return (
      <Box
        sx={{
          minHeight: '100dvh',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
          bgcolor: '#e8f5e9',
          userSelect: 'none',
          p: 4,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 960, display: 'flex', justifyContent: 'flex-start' }}>
          <Tooltip title="Panele dön">
            <IconButton
              onClick={handleBack}
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.7)',
                border: '1px solid rgba(0,0,0,0.08)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#2e7d32', mb: 1 }}>
          🌱 GELECEK ORMANI
        </Typography>
        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 2 }}>
          Sınıfınızı seçin
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center', maxWidth: 600 }}>
          {classes.map((cls) => (
            <Button
              key={cls.id || cls.name}
              variant="contained"
              onClick={() => setClassName(cls.name)}
              sx={{
                px: 4,
                py: 2,
                fontSize: 18,
                fontWeight: 700,
                borderRadius: 3,
                textTransform: 'none',
                background: 'linear-gradient(135deg, #66bb6a, #43a047)',
                color: '#fff',
                minWidth: 120,
                '&:hover': {
                  background: 'linear-gradient(135deg, #4caf50, #388e3c)',
                },
              }}
            >
              {cls.name}
            </Button>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        width: '100%',
        px: { xs: 1.5, sm: 2.2, md: 3 },
        py: { xs: 1.5, md: 2.5 },
        background: 'linear-gradient(180deg, #e7f7ff 0%, #d9f2e5 55%, #fef6da 100%)',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.7 }}>
        <Paper
          elevation={2}
          sx={{
            borderRadius: isUyanis ? 3 : 4,
            p: { xs: 1.4, sm: 1.8 },
            minHeight: isUyanis ? { xs: 150, sm: 168 } : undefined,
            bgcolor: bannerMeta.bannerBg,
            border: `1px solid ${bannerMeta.bannerBorder}`,
            position: 'relative',
            overflow: 'hidden',
            animation: phaseFxOn ? `${phaseBloom} 0.9s ease-out` : 'none',
          }}
        >
          {activeLeaves.map((leaf, idx) => (
            <Box
              key={`leaf-${idx}`}
              sx={{
                position: 'absolute',
                left: leaf.left,
                top: leaf.top,
                opacity: 0.14,
                fontSize: 18,
                pointerEvents: 'none',
                animation: `${leafDrift} ${leaf.duration} ease-in-out ${leaf.delay} infinite`,
              }}
            >
              🍃
            </Box>
          ))}

          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: bannerMeta.landscapeBg,
              pointerEvents: 'none',
            }}
          />

          {phase === 'uyanis' && (
            <>
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: 64,
                  background: 'linear-gradient(180deg, rgba(164,204,126,0.72) 0%, rgba(142,188,108,0.84) 100%)',
                  clipPath: 'polygon(0 40%, 18% 30%, 36% 24%, 56% 28%, 74% 34%, 100% 46%, 100% 100%, 0 100%)',
                  pointerEvents: 'none',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: 24,
                  background: 'linear-gradient(180deg, rgba(138,99,74,0.84) 0%, rgba(111,79,61,0.92) 100%)',
                  pointerEvents: 'none',
                }}
              />
            </>
          )}

          {phase === 'uyanis' &&
            uyanisSprouts.map((sprout, idx) => (
              <Box
                key={`uyanis-sprout-wide-${idx}`}
                sx={{
                  position: 'absolute',
                  left: `${sprout.left}%`,
                  bottom: Number(sprout.bottom || 20) + 5,
                  width: 16,
                  height: 26,
                  transform: `translateX(-50%) scale(${sprout.scale})`,
                  transformOrigin: 'bottom center',
                  animation: `${sproutSway} 3.9s ease-in-out ${sprout.delay} infinite`,
                  pointerEvents: 'none',
                  zIndex: 1,
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: 7,
                    bottom: 0,
                    width: 2,
                    height: 13,
                    bgcolor: '#4caf50',
                    borderRadius: 99,
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    left: -1,
                    bottom: 10,
                    width: 10,
                    height: 7,
                    bgcolor: '#8bc34a',
                    borderRadius: '90% 10% 80% 20%',
                    transform: 'rotate(-24deg)',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    left: 7,
                    bottom: 10,
                    width: 10,
                    height: 7,
                    bgcolor: '#7cb342',
                    borderRadius: '10% 90% 20% 80%',
                    transform: 'rotate(22deg)',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    left: 12,
                    bottom: 18,
                    width: 4,
                    height: 4,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.92) 0%, rgba(144,202,249,0.62) 70%, rgba(144,202,249,0.08) 100%)',
                    animation: `${dewTwinkle} 2.8s ease-in-out ${sprout.delay} infinite`,
                  }}
                />
              </Box>
            ))}

          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              alignItems: isUyanis ? 'flex-start' : 'flex-end',
              justifyContent: 'space-between',
              gap: 2,
              minHeight: isUyanis ? { xs: 118, sm: 132 } : undefined,
            }}
          >
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 800, color: '#2e7d32' }}>
                {phaseIcon} {bannerMeta.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {bannerMeta.subtitle}
              </Typography>
              {phase !== 'uyanis' && (
                <Box
                  sx={{
                    mt: 0.7,
                    display: 'flex',
                    gap: 0.5,
                    transformOrigin: 'bottom center',
                    animation: `${forestSway} ${bannerMeta.swayDuration} ease-in-out infinite`,
                  }}
                >
                  {bannerMeta.icons.map((icon, idx) => (
                    <Box
                      key={`phase-icon-${idx}`}
                      sx={{ fontSize: idx >= 2 ? 28 : 24 + idx, lineHeight: 1 }}
                    >
                      {icon}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {dropFx.show && (
              <Box
                sx={{
                  position: 'relative',
                  animation: `${dropPop} 1.8s ease-out`,
                }}
              >
                <Chip
                  label="+1 damla"
                  color="success"
                  sx={{
                    fontWeight: 800,
                    boxShadow: '0 0 0 6px rgba(76,175,80,0.12)',
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    top: -16,
                    right: 4,
                    color: '#2e7d32',
                    fontWeight: 800,
                    animation: `${sparkleDrift} 1.5s ease-out`,
                    pointerEvents: 'none',
                  }}
                >
                  ✨
                </Typography>
                {Number(dropFx.amount || 0) > 1 && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      textAlign: 'right',
                      color: 'text.secondary',
                      mt: 0.4,
                      fontWeight: 700,
                    }}
                  >
                    +{Number(dropFx.amount || 0)} toplam yeni damla
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Paper>

        <Paper
          elevation={3}
          sx={{
            borderRadius: 4,
            p: { xs: 1.2, sm: 1.5, md: 2 },
            bgcolor: 'rgba(255,255,255,0.86)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            gap: 1.2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
            <Tooltip title="Panele dön">
              <IconButton
                onClick={handleBack}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.7)',
                  border: '1px solid rgba(0,0,0,0.08)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
                }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography sx={{ fontWeight: 900, fontSize: 'clamp(28px, 3.3vw, 52px)', lineHeight: 1 }}>
              🌱 Gelecek Ormanı
            </Typography>
            {className && (
              <Chip
                label={`Sınıf: ${className}`}
                sx={{ fontWeight: 700, bgcolor: 'rgba(46,125,50,0.12)', color: '#2e7d32' }}
              />
            )}
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {classes.length > 1 && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setClassName('')}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  fontWeight: 600,
                  borderColor: '#ff9800',
                  color: '#e65100',
                  '&:hover': { bgcolor: '#fff3e0', borderColor: '#e65100' },
                }}
              >
                Sınıf Değiştir
              </Button>
            )}
            <Button variant="contained" size="small" onClick={load} sx={{ textTransform: 'none', borderRadius: 2 }}>
              Yenile
            </Button>
          </Stack>
        </Paper>

        {!!error && (
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Tekrar dene</Button>}>
            {error}
          </Alert>
        )}

        {!!sendInfo && (
          <Alert severity="success" onClose={() => setSendInfo('')}>
            {sendInfo}
          </Alert>
        )}

        {(String(capabilities?.monthly_book_count || '') === 'missing' ||
          String(capabilities?.bulk_motivation_send || '') === 'not_implemented') && (
          <Alert severity="info">
            Faz-2 notu: gelişmiş kitle segmentleri ve hazır şablon havuzu bir sonraki adımda genişletilecek.
          </Alert>
        )}

        <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.9)' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                type="month"
                label="Ay"
                value={monthKey}
                onChange={(e) => setMonthKey(String(e.target.value || ''))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Nabız Tarihi"
                value={localDate}
                onChange={(e) => setLocalDate(String(e.target.value || ''))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1} sx={{ height: '100%', alignItems: 'center' }}>
                <Chip label={monthLabel} color="success" variant="outlined" sx={{ fontWeight: 700 }} />
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <SectionCard
              title={`${phaseIcon} ${className || '-'} Gelecek Ormanı`}
              subtitle={`${monthLabel} • Evre: ${phaseTitle}`}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.2, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Sınıfımız bu ay
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                    💧 {heroDropsTotal} damla topladı
                  </Typography>
                </Box>
                <Chip label={`Evre: ${phaseTitle}`} color="success" variant="outlined" sx={{ fontWeight: 700 }} />
              </Stack>

              <Box sx={{ display: 'flex', gap: 0.8, mb: 1.5 }}>
                {forestGlyphs.map((glyph, idx) => (
                  <Typography key={`forest-glyph-${idx}`} sx={{ fontSize: 24, lineHeight: 1 }}>
                    {glyph}
                  </Typography>
                ))}
              </Box>

              <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                <Grid item xs={6} md={4}>
                  <Card sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ py: 1.4 }}>
                      <Typography variant="caption" color="text.secondary">Damla</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800 }}>
                        {heroDropsTotal}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={4}>
                  <Card sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ py: 1.4 }}>
                      <Typography variant="caption" color="text.secondary">Hedef</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800 }}>
                        {heroTargetDrops}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={4}>
                  <Card sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ py: 1.4 }}>
                      <Typography variant="caption" color="text.secondary">İlerleme</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800 }}>
                        %{progressPct.toFixed(1)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.7 }}>
                Damla ilerlemesi: {heroDropsTotal} / {heroTargetDrops}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.max(0, Math.min(100, progressPct))}
                sx={{ height: 10, borderRadius: 999, mb: 1.4 }}
              />
              <Typography variant="body2" color="text.secondary">
                {getHeroComment(progressPct)}
              </Typography>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={5}>
            <SectionCard title="📊 Haftalık Sınıf Nabzı" subtitle={`${localDate} itibarıyla son 7 gün`}>
              <Stack direction="row" spacing={1} sx={{ mb: 1.4, flexWrap: 'wrap' }} useFlexGap>
                <Chip
                  color={pulseColorByLevel[String(weeklyPulseStatus?.level || 'medium')] || 'info'}
                  label={String(weeklyPulseStatus?.message || 'Haftalık ritim hesaplanıyor.')}
                />
              </Stack>

              <Typography variant="body2" sx={{ fontWeight: 700, mb: 1.2 }}>
                {getWeeklyPulseNarrative(weeklyActiveParents, weeklyDrops)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.4 }}>
                Bu görünüm sınıfın son 7 günlük ritmini özetler.
              </Typography>

              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Card sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ py: 1.4 }}>
                      <Typography variant="caption" color="text.secondary">Son 7 gün aktif veli</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 800 }}>
                        {weeklyActiveParents}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Geçen hafta: {previousWeeklyActiveParents}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6}>
                  <Card sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ py: 1.4 }}>
                      <Typography variant="caption" color="text.secondary">Son 7 gün damla</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 800 }}>
                        {weeklyDrops}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Geçen hafta: {previousWeeklyDrops}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard
              title="Son 7 Gün Trendi"
              subtitle={getTrendComment(trend?.active_parents_sum, trend?.previous_7_active_parents_sum)}
            >
              {!Array.isArray(trend?.items) || trend.items.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Henüz trend gösterecek kadar veri oluşmadı.
                </Typography>
              ) : (
                <TrendBars items={trend.items} />
              )}
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={3}>
            <SectionCard title="Katılım Dağılımı" subtitle="Aylık aktif gün segmentleri">
              <Stack spacing={1.1}>
                <Chip color="success" label={`Çok aktif (15+): ${Number(distribution?.high_segment_count || 0)}`} />
                <Chip color="warning" label={`Orta (5-14): ${Number(distribution?.medium_segment_count || 0)}`} />
                <Chip color="default" label={`Düşük (0-4): ${Number(distribution?.low_segment_count || 0)}`} />
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={3}>
            <SectionCard title="Müdahale Önerileri" subtitle="Sınıf ritmine göre öneriler">
              {!suggestions.length ? (
                <Typography variant="body2" color="text.secondary">
                  Şu an ek öneri gerektiren bir durum görünmüyor.
                </Typography>
              ) : (
                <Stack spacing={1.2}>
                  {suggestions.map((item, idx) => {
                    const meta = getSuggestionMeta(item?.type);
                    return (
                      <Paper
                        key={`${item?.type || 's'}-${idx}`}
                        variant="outlined"
                        sx={{ p: 1.2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.7)' }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.4 }}>
                          {String(meta.title || 'Öneri')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          {String(meta.body || '')}
                        </Typography>
                        <Button size="small" variant="text" sx={{ textTransform: 'none' }} onClick={() => handleOpenSuggestion(item)}>
                          Mesaj Gönder
                        </Button>
                      </Paper>
                    );
                  })}
                </Stack>
              )}
            </SectionCard>
          </Grid>

          <Grid item xs={12}>
            <SectionCard
              title="🏅 Sistemin Önerdiği Ayın Velisi"
              subtitle={className ? `${className} • ${monthLabel}` : 'Önce sınıf seçiniz'}
              action={
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleOpenAppreciationMessage}
                  disabled={!appreciationCandidate}
                  sx={{ textTransform: 'none' }}
                >
                  Teşekkür Mesajı Gönder
                </Button>
              }
            >
              {!className ? (
                <Typography variant="body2" color="text.secondary">
                  Öneri için önce sınıf seçimi yapın.
                </Typography>
              ) : appreciationLoading ? (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Adaylar değerlendiriliyor...
                  </Typography>
                </Stack>
              ) : appreciationCandidate ? (
                <Stack spacing={1.1}>
                  <Stack direction="row" spacing={1.2} sx={{ alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: 'success.light', color: 'success.dark', width: 32, height: 32 }}>
                      #{Number(appreciationCandidate?.rank || 1)}
                    </Avatar>
                    <Typography variant="body1" sx={{ fontWeight: 800 }}>
                      {String(appreciationCandidate?.parent_first_name || '-')} velisi
                    </Typography>
                    <Chip size="small" color="success" label={`Öğrenci: ${String(appreciationCandidate?.student_first_name || '-')}`} />
                  </Stack>

                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip size="small" label={`Aylık puan: ${Number(appreciationCandidate?.monthly_vks_points || 0)}`} />
                    <Chip size="small" label={`Aktif gün: ${Number(appreciationCandidate?.monthly_active_days || 0)}`} />
                    <Chip size="small" label={`Seri: ${Number(appreciationCandidate?.current_streak || 0)} gün`} />
                    <Chip size="small" label={`Son aktif: ${String(appreciationCandidate?.last_active_date || '-')}`} />
                  </Stack>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Bu ay için önerilen ayın velisi adayı henüz oluşmadı.
                </Typography>
              )}

              {!!appreciationError && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                  {appreciationError}
                </Typography>
              )}
            </SectionCard>
          </Grid>

          <Grid item xs={12}>
            <SectionCard
              title="Detay Veli Listesi"
              subtitle="Risk odaklı sıralama"
            >
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.4 }}>
                <Chip label="Tümü" color={parentFilter === 'all' ? 'primary' : 'default'} onClick={() => setParentFilter('all')} />
                <Chip label="Aktif" color={parentFilter === 'active' ? 'primary' : 'default'} onClick={() => setParentFilter('active')} />
                <Chip label="Henüz Düzenli Değil" color={parentFilter === 'takip_yok' ? 'primary' : 'default'} onClick={() => setParentFilter('takip_yok')} />
                <Chip label="Düşüş" color={parentFilter === 'dusus' ? 'primary' : 'default'} onClick={() => setParentFilter('dusus')} />
                <Chip label="Teşekkür Alan" color={parentFilter === 'awarded' ? 'primary' : 'default'} onClick={() => setParentFilter('awarded')} />
              </Stack>

              {!filteredParents.length ? (
                <Typography variant="body2" color="text.secondary">
                  Seçilen filtre için veli kaydı bulunamadı.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Öğrenci</TableCell>
                        <TableCell>Veli</TableCell>
                        <TableCell align="right">Aktif Gün</TableCell>
                        <TableCell align="right">Puan</TableCell>
                        <TableCell>Seri</TableCell>
                        <TableCell align="right">Mazeret</TableCell>
                        <TableCell align="right">Bu Ay Kitap</TableCell>
                        <TableCell>Son Aktif</TableCell>
                        <TableCell>Risk</TableCell>
                        <TableCell>Teşekkür</TableCell>
                        <TableCell align="right">Aksiyon</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredParents.map((row) => {
                        const riskKey = String(row?.risk_bucket || 'aktif');
                        const risk = riskMeta[riskKey] || riskMeta.aktif;
                        return (
                          <TableRow key={`${row?.student_id || 0}-${row?.parent_id || 0}`} hover>
                            <TableCell>{String(row?.student_name || '-')}</TableCell>
                            <TableCell>{String(row?.parent_name || '-')}</TableCell>
                            <TableCell align="right">{Number(row?.monthly_active_days || 0)}</TableCell>
                            <TableCell align="right">{Number(row?.monthly_points || 0)}</TableCell>
                            <TableCell>
                              <Typography variant="caption" sx={{ display: 'block' }}>
                                🔥 {Number(row?.current_streak || 0)} gün
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                En iyi: {Number(row?.best_streak || 0)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{Number(row?.excuse_balance || 0)}</TableCell>
                            <TableCell align="right">{Number(row?.monthly_book_count || 0)}</TableCell>
                            <TableCell>{String(row?.last_active_date || '-')}</TableCell>
                            <TableCell>
                              <Chip size="small" color={risk.color} label={risk.label} />
                            </TableCell>
                            <TableCell>
                              {row?.teacher_appreciation_awarded ? (
                                <Chip size="small" color="success" label="Verildi" />
                              ) : (
                                <Chip size="small" variant="outlined" label="-" />
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                variant="outlined"
                                sx={{ textTransform: 'none' }}
                                onClick={() => handleOpenRowMessage(row)}
                              >
                                Mesaj
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </SectionCard>
          </Grid>

          <Grid item xs={12}>
            <SectionCard title="Başarı Arşivi" subtitle="Son 6 ay görünümü">
              {!archive.length ? (
                <Typography variant="body2" color="text.secondary">
                  Henüz arşiv verisi yok.
                </Typography>
              ) : (
                <Grid container spacing={1.2}>
                  {archive.map((row, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={2} key={`${row?.month_key || 'm'}-${index}`}>
                      <Card sx={{ borderRadius: 2 }}>
                        <CardContent sx={{ py: 1.3 }}>
                          <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            {String(row?.month_key || '-')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {phaseLabel(row?.phase)}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.8 }}>
                            {Number(row?.drops_total || 0)} / {Number(row?.target_drops || 0)}
                          </Typography>
                          <Chip
                            size="small"
                            sx={{ mt: 0.8 }}
                            color={row?.achieved_target ? 'success' : 'default'}
                            label={row?.achieved_target ? 'Hedefe ulaşıldı' : `%${Number(row?.progress_pct || 0).toFixed(1)}`}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </SectionCard>
          </Grid>
        </Grid>
      </Box>

      <Dialog open={!!messageDialog?.open} onClose={() => setMessageDialog((prev) => ({ ...prev, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>{String(messageDialog?.title || 'Motivasyon Mesajı')}</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            size="small"
            label="Hedef Kitle"
            value={String(messageDialog?.audience || 'all_parents')}
            onChange={(e) => {
              const value = String(e.target.value || 'all_parents');
              setMessageDialog((prev) => ({
                ...prev,
                audience: value,
                studentId: value === 'selected_parent' ? prev.studentId : null,
                parentId: value === 'selected_parent' ? prev.parentId : null,
              }));
            }}
            disabled={!!messageDialog?.lockedAudience}
            SelectProps={{ native: true }}
            sx={{ mb: 1 }}
          >
            <option value="all_parents">{audienceMeta.all_parents}</option>
            <option value="low_segment">{audienceMeta.low_segment}</option>
            <option value="selected_parent">{audienceMeta.selected_parent}</option>
          </TextField>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Seçim: {String(audienceMeta[String(messageDialog?.audience || 'all_parents')] || audienceMeta.all_parents)}
          </Typography>
          <TextField
            multiline
            minRows={4}
            fullWidth
            value={String(messageDialog?.body || '')}
            onChange={(e) => setMessageDialog((prev) => ({ ...prev, body: String(e.target.value || '') }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMessageDialog((prev) => ({ ...prev, open: false }))}>Kapat</Button>
          <Button variant="outlined" onClick={handleCopyMessage}>Mesajı Kopyala</Button>
          <Button variant="contained" onClick={handleSendMessage} disabled={sendLoading}>
            {sendLoading ? 'Gönderiliyor...' : 'Mesajı Gönder'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
